import { z } from 'zod';

// Authentication schemas
export const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
});

export const otpLoginSchema = z.object({
  email: z.string().email(),
});

export const otpVerifySchema = z.object({
  token: z.string().length(6, 'Le code OTP doit contenir 6 chiffres'),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email('Email invalide'),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Token requis'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Mot de passe actuel requis'),
  newPassword: z.string().min(8, 'Le nouveau mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'Le nouveau mot de passe doit contenir au moins 8 caractères'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

// Profile schemas
export const updateProfileSchema = z.object({
  firstName: z.string().min(2, 'Prénom trop court'),
  lastName: z.string().min(2, 'Nom trop court'),
  phone: z.string().min(10, 'Numéro de téléphone invalide'),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
});

export const addDependentSchema = z.object({
  firstName: z.string().min(2, 'Prénom requis'),
  lastName: z.string().min(2, 'Nom requis'),
  dateOfBirth: z.string().min(1, 'Date de naissance requise'),
  gender: z.enum(['male', 'female', 'other'], {
    errorMap: () => ({ message: 'Genre requis' }),
  }),
  relationshipType: z.enum(['spouse', 'child', 'other'], {
    errorMap: () => ({ message: 'Lien de parenté requis' }),
  }),
  phone: z.string().optional(),
});

// Claim schemas
export const createClaimSchema = z.object({
  benefitId: z.string().min(1, 'Garantie requise'),
  type: z.enum(['medical', 'dental', 'optical', 'pharmaceutical', 'hospitalization']),
  amount: z.number().min(0, 'Montant invalide'),
  treatmentDate: z.string().min(1, 'Date de traitement requise'),
  providerName: z.string().min(2, 'Nom du prestataire requis'),
  description: z.string().optional(),
});

// Support ticket schemas
export const createTicketSchema = z.object({
  subject: z.string().min(5, 'Sujet trop court (min 5 caractères)'),
  category: z.enum(['technical', 'billing', 'claim', 'account', 'general']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  description: z.string().min(20, 'Description trop courte (min 20 caractères)'),
});

export const addTicketMessageSchema = z.object({
  message: z.string().min(1, 'Message requis'),
});

// Contact change request schema
export const contactChangeSchema = z.object({
  field: z.enum(['phone', 'email', 'address']),
  oldValue: z.string().min(1, 'Ancienne valeur requise'),
  newValue: z.string().min(1, 'Nouvelle valeur requise'),
  reason: z.string().optional(),
});

// Type inference helpers
export type LoginFormData = z.infer<typeof loginSchema>;
export type OTPLoginFormData = z.infer<typeof otpLoginSchema>;
export type OTPVerifyFormData = z.infer<typeof otpVerifySchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type UpdateProfileFormData = z.infer<typeof updateProfileSchema>;
export type AddDependentFormData = z.infer<typeof addDependentSchema>;
export type CreateClaimFormData = z.infer<typeof createClaimSchema>;
export type CreateTicketFormData = z.infer<typeof createTicketSchema>;
export type AddTicketMessageFormData = z.infer<typeof addTicketMessageSchema>;
export type ContactChangeFormData = z.infer<typeof contactChangeSchema>;
